import { FC, useCallback, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import {
  AxisTransformer,
  getDefaultChartOptions,
  getDefaultChartPlugins,
  TooltipBodyConfig,
} from '../../utils';
import { ChartData, ChartDataset, ChartOptions, Point } from 'chart.js';
import { ChartPoint, ChartSeries } from '../../types';
import { DEFAULT_COLORS } from '../../constants';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

const transformDataForChart = (data: ChartSeries[]): ChartData<'bar'> => {
  const barCharts: ChartDataset<'bar', (number | Point | [number, number] | null)[]>[] = [];

  for (const [index, chart] of data.entries()) {
    const chartData: ChartDataset<'bar', (number | [number, number] | null)[]> = {
      label: chart.name,
      data: chart.points.map((item: ChartPoint) => Number(item.y)),
      backgroundColor: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    };

    barCharts.push(chartData);
  }

  return {
    labels: data[0].points.map((item: ChartPoint) => item.x),
    datasets: barCharts as ChartDataset<'bar', (number | [number, number] | null)[]>[],
  };
};

interface BarChartProps {
  data: ChartSeries[];
  chartId: string;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisTransformer?: AxisTransformer;
  yAxisTransformer?: AxisTransformer;
  threshold?: number;
  thresholdLabel?: string;
  renderForExport?: boolean;
}

const BarChart: FC<BarChartProps> = ({
  data,
  chartId,
  xAxisLabel,
  yAxisLabel,
  xAxisTransformer,
  yAxisTransformer,
  threshold,
  thresholdLabel,
  renderForExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  // Callback ref to assign the ref value dynamically
  const chartRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  // Use the intersection observer hook for performance
  const isVisible = useIntersectionObserver(chartNode);

  const chartData = transformDataForChart(data);

  const xAxisTicksCallback = (value: any, index: number) => {
    return chartData.labels?.[index] || value;
  };

  let footerConfigs: TooltipBodyConfig[] = [];
  if (threshold) {
    footerConfigs = [
      {
        type: 'indicator',
        backgroundColor: '#162C36',
      },
      {
        type: 'text',
        text: thresholdLabel || 'Threshold',
      },
      {
        type: 'text-value',
        value: {
          threshold: threshold || 0,
        },
        key: 'threshold',
        transformer: yAxisTransformer,
      },
    ];
  }

  const chartOptions = getDefaultChartOptions({
    xAxisLabel,
    showXGrid: false,
    xAxisTransformationFuntion: xAxisTransformer,
    yAxisTransformationFuntion: yAxisTransformer,
    xAxisTicksCallback,
    threshold,
    tooltipConfigs: {
      bodyConfigs: [
        {
          type: 'indicator',
        },
        {
          type: 'label',
        },
        {
          type: 'value',
        },
      ],
      footerConfigs,
    },
  });

  const chartPlugins = getDefaultChartPlugins({
    xLabel: xAxisLabel,
    yLabel: yAxisLabel,
  });

  return (
    <Box
      id={`pvx-bar-chart-container-${chartId}`}
      ref={chartRef}
      sx={{ height: isMobile ? '400px' : '620px', width: '100%' }}
    >
      {renderForExport ? (
        <Bar
          data={chartData}
          options={chartOptions as ChartOptions<'bar'>}
          plugins={chartPlugins}
        />
      ) : (
        isVisible && (
          <Bar
            data={chartData}
            options={chartOptions as ChartOptions<'bar'>}
            plugins={chartPlugins}
          />
        )
      )}
    </Box>
  );
};

export default BarChart;
